import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { formatNumber } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { FirstArticles } from './FirstArticles';
import { Ppaps } from './Ppaps';
import { Spc } from './Spc';

type Tab = 'fai' | 'ppap' | 'spc';
interface FaiSummary {
  drafts: number;
  approvedThisMonth: number;
  withDeviation: number;
  rejectedThisMonth: number;
}
interface PpapSummary {
  drafts: number;
  withCustomer: number;
  approved: number;
  interim: number;
  rejected: number;
}

// First article inspection, production part approval and statistical process control: what proves
// that a part can be made right, before and while it is made in numbers
const PartApproval: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('quality.create');
  const canUpdate = can('quality.update');
  const canDelete = can('quality.delete');
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('fai');
  const { data: fai } = useQuery({ queryKey: ['part-approval', 'fai-summary'], queryFn: async (): Promise<FaiSummary> => (await api.get('/first-articles/summary')).data });
  const { data: ppap } = useQuery({ queryKey: ['part-approval', 'ppap-summary'], queryFn: async (): Promise<PpapSummary> => (await api.get('/ppaps/summary')).data });
  const refreshSummary = () => queryClient.invalidateQueries({ queryKey: ['part-approval'] });

  const stats = [
    { label: 'First articles to decide', value: formatNumber(fai?.drafts ?? 0), hint: `${formatNumber(fai?.approvedThisMonth ?? 0)} approved this month · ${formatNumber(fai?.rejectedThisMonth ?? 0)} rejected` },
    { label: 'Approved with deviation', value: formatNumber(fai?.withDeviation ?? 0), hint: 'First articles accepted on a concession' },
    { label: 'PPAP with the customer', value: formatNumber(ppap?.withCustomer ?? 0), hint: `${formatNumber(ppap?.drafts ?? 0)} being put together` },
    { label: 'PPAP approved', value: formatNumber(ppap?.approved ?? 0), hint: `${formatNumber(ppap?.interim ?? 0)} interim · ${formatNumber(ppap?.rejected ?? 0)} rejected` },
  ];
  const tabs: { key: Tab; label: string }[] = [
    { key: 'fai', label: 'First articles' },
    { key: 'ppap', label: 'PPAP' },
    { key: 'spc', label: 'SPC' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Part Approval &amp; SPC</h1>
        <p className="mt-1 text-sm text-gray-500">First article inspection of new and changed parts, PPAP submissions to customers, and control charts of the characteristics that are measured in production.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {item.label}
            </button>
          ))}
        </div>
        {tab === 'fai' && <FirstArticles canCreate={canCreate} canDecide={canUpdate} canDelete={canDelete} onChanged={refreshSummary} />}
        {tab === 'ppap' && <Ppaps canCreate={canCreate} canEdit={canUpdate} canDelete={canDelete} onChanged={refreshSummary} />}
        {tab === 'spc' && <Spc canCreate={canCreate} canRecord={canUpdate} canDelete={canDelete} />}
      </div>
    </div>
  );
};

export default PartApproval;
